import { useMemo, useState, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { LIST_PAGE_SHELL } from "@/components/layout/pageShell";
import { useCustomerListWithPagination } from "./api/customerHooks";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { buildNewRoute } from "@/lib/config/docRoutes";
import { injectActionsColumn } from "@/features/list/actionsColumn";
import { GenericInlineDetailPanel } from "@/features/list/GenericInlineDetailPanel";
import { GenericDetailPanel } from "@/features/list/GenericDetailPanel";
import { useDetailPanelState } from "@/features/list/useDetailPanelState";
import { Users } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";

interface CustomerListItem {
  name: string;
  customer_name: string;
  customer_type?: string;
  customer_group?: string;
  territory?: string;
  email_id?: string;
  phone?: string;
  mobile_no?: string;
  image?: string;
  modified?: string;
  creation?: string;
}

export function CustomersListPage() {
  const navigate = useNavigate();
  const [inlineDetailDocName, setInlineDetailDocName] = useState<string | null>(null);
  const handleInlineClose = useCallback(() => setInlineDetailDocName(null), []);
  const { detailPanelItem, panelItem, openDetailPanel, closeDetailPanel } = useDetailPanelState();

  const renderSubRow = useCallback(
    (row: CustomerListItem, columnInfo: SubRowColumnInfo): ReactNode | null => {
      if (!row.name || row.name !== inlineDetailDocName) return null;
      return (
        <GenericInlineDetailPanel
          key={row.name}
          doctype="Customer"
          docName={row.name}
          rowData={row as unknown as Record<string, unknown>}
          onClose={handleInlineClose}
          columns={columnInfo}
        />
      );
    },
    [inlineDetailDocName, handleInlineClose],
  );

  const baseColumns = useMemo<ColumnDef<CustomerListItem, unknown>[]>(
    () => [
      {
        id: "customer_name",
        accessorKey: "customer_name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Customer Name" />
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <div className="font-medium">{row.getValue("customer_name")}</div>
        ),
      },
      {
        id: "customer_type",
        accessorKey: "customer_type",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Type" />
        ),
        enableSorting: true,
        cell: ({ row }) => row.getValue("customer_type") || "-",
      },
      {
        id: "customer_group",
        accessorKey: "customer_group",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Group" />
        ),
        enableSorting: true,
        cell: ({ row }) => row.getValue("customer_group") || "-",
      },
      {
        id: "email_id",
        accessorKey: "email_id",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Email" />
        ),
        enableSorting: true,
        cell: ({ row }) => row.getValue("email_id") || "-",
      },
      {
        id: "phone",
        accessorKey: "phone",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Phone" />
        ),
        enableSorting: true,
        cell: ({ row }) => {
          const phone = row.getValue("phone") as string | undefined;
          const mobile = row.original.mobile_no;
          return phone || mobile || "-";
        },
      },
    ],
    []
  );

  const columns = useMemo(
    () => injectActionsColumn(baseColumns, {
      onToggleDetail: (name) => setInlineDetailDocName((prev) => prev === name ? null : name),
      onOpen: (name) => openDetailPanel(name),
    }),
    [baseColumns]
  );

  return (
    <div className={LIST_PAGE_SHELL}>
      <div>
        <DocTypeListPageShell<CustomerListItem>
          doctype="Customer"
          columns={columns}
          filterStoreKey="crm.customers"
          useListHook={useCustomerListWithPagination}
          defaultSorting={[{ id: "modified", desc: true }]}
          pageSizeOptions={[20, 50, 100, 200, 400, 500]}
          searchPlaceholder="Search customers..."
          title="Customers"
          newButtonLabel="New Customer"
          onNewClick={() => navigate(buildNewRoute("Customer"))}
          onRowClick={undefined}
          renderSubRow={renderSubRow}
          activeSubRowId={inlineDetailDocName ?? undefined}
          getRowId={(row) => row.name}
          inlineEditable
          isInlineEditMode
          compactMode
          noPadding
          fullWidth
          className="pt-0"
          tableContainerClassName="overflow-x-auto overflow-y-hidden border-y border-border bg-white dark:bg-neutral-950"
          thumbnailConfig={{
            imageFieldnames: ["image"],
            labelFieldnames: ["customer_name", "name"],
            sizePx: 28,
          }}
          galleryConfig={{
            sizePx: 160,
            titleFieldnames: ["customer_name"],
            subtitleFieldnames: ["customer_group", "customer_type"],
          }}
          breadcrumbs={[
            {
              label: "Customers",
              icon: (
                <span className="flex items-center justify-center h-5 w-5 rounded-xs bg-violet-500 shrink-0">
                  <Users className="h-3 w-3 text-white" />
                </span>
              ),
            },
          ]}
          enableViewModeSwitch={true}
          listSettingsConfig={{
            key: "crm.customers",
            meta: {
              doctype: "Customer",
              includeFieldtypes: [
                "Data",
                "Link",
                "Select",
                "Phone",
                "Date",
                "Datetime",
                "Check",
                "Small Text",
                "Text",
              ],
              excludeFieldtypes: [
                "Section Break",
                "Column Break",
                "HTML",
                "Table",
                "Table MultiSelect",
                "Button",
                "Heading",
              ],
              excludeFieldnames: [
                "naming_series",
                "owner",
                "creation",
                "modified",
                "modified_by",
                "idx",
                "__islocal",
                "__unsaved",
                "_assign",
                "_comment_count",
                "_liked_by",
              ],
              requiredFieldnames: ["customer_name"],
              defaultVisibleFieldnames: [
                "customer_name",
                "customer_type",
                "customer_group",
                "email_id",
                "phone",
              ],
            },
            staticFields: [
              {
                fieldname: "__thumbnail",
                label: "Photo",
                defaultVisible: true,
              },
            ],
          }}
        />
      </div>

      <GenericDetailPanel
        open={!!detailPanelItem}
        onClose={closeDetailPanel}
        doctype="Customer"
        docName={panelItem?.name ?? ""}
        docLabel={panelItem?.label}
      />
    </div>
  );
}
